/**
 * feat-303-curriculum-drip-release — pure helpers for the admin drip controls.
 *
 * Nothing here computes drip RELEASE (who sees what): that is the backend's
 * `services/lesson_drip.py`. These only validate the rate the admin types,
 * resolve the inherited setting for DISPLAY (the same field-by-field `??` as
 * the backend's `effective_drip_settings`), and build the wire bodies.
 */

/** Mirrors the backend's DRIP_RATE_MIN / DRIP_RATE_MAX (StrictInt, ge/le). */
export const DRIP_RATE_MIN = 1;
export const DRIP_RATE_MAX = 20;

/** Parse a typed rate. Returns the integer, or null for anything the backend
 *  would 422: empty, non-integer ("2.5", "1e1"), or outside 1–20. */
export function parseDripRate(raw: string): number | null {
  const s = raw.trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isInteger(n) || n < DRIP_RATE_MIN || n > DRIP_RATE_MAX) return null;
  return n;
}

export interface TenantDrip {
  dripEnabled?: boolean;
  dripLessonsPerDay?: number;
}

export interface LocationDrip {
  dripEnabled?: boolean | null;
  dripLessonsPerDay?: number | null;
}

/** The effective setting a location's learners get. Each field resolves
 *  independently (DESIGN-QUESTION.md Q4), exactly like the backend helper. */
export function effectiveLocationDrip(loc: LocationDrip, tenant: TenantDrip) {
  const enabled = loc.dripEnabled ?? !!tenant.dripEnabled;
  const rate = loc.dripLessonsPerDay ?? tenant.dripLessonsPerDay ?? 1;
  // "(override)" follows the on/off field: that is the choice an admin makes in
  // the modal. A stray rate under an inherited on/off (Q4) still reads
  // "(inherited)" but shows the true effective rate.
  const overridden = loc.dripEnabled !== null && loc.dripEnabled !== undefined;
  return { enabled, rate, overridden };
}

export type LocationDripChoice = 'inherit' | 'on' | 'off';

/** What the Edit Location modal opens on. A null on/off is Inherit even when a
 *  stray rate is stored (Q4); saving Inherit then clears that rate. */
export function locationDripChoice(loc: LocationDrip): LocationDripChoice {
  if (loc.dripEnabled === true) return 'on';
  if (loc.dripEnabled === false) return 'off';
  return 'inherit';
}

export interface LocationDripBody {
  drip_enabled: boolean | null;
  drip_lessons_per_day: number | null;
}

/** NOTES.md §3 wire mapping. Both keys always present, explicit null for
 *  inherit. `rate` must already be validated when `choice === 'on'`. */
export function locationDripBody(choice: LocationDripChoice, rate: number | null): LocationDripBody {
  if (choice === 'on') return { drip_enabled: true, drip_lessons_per_day: rate };
  if (choice === 'off') return { drip_enabled: false, drip_lessons_per_day: null };
  return { drip_enabled: null, drip_lessons_per_day: null };
}

/** True when the body would change what is stored. */
export function locationDripChanged(loc: LocationDrip, body: LocationDripBody): boolean {
  return (
    (loc.dripEnabled ?? null) !== body.drip_enabled ||
    (loc.dripLessonsPerDay ?? null) !== body.drip_lessons_per_day
  );
}
